import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { formatDate, textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import DocumentConfirmModal from "./DocumentConfirmModal";
import DocumentHistoryModal from "./DocumentHistoryModal";
import DocumentFormModal, { DOCUMENT_SITES, DOCUMENT_STATUSES } from "./DocumentFormModal";

const PAGE_SIZE = 10;
const siteName = (key) => DOCUMENT_SITES.find((s) => s.key === key)?.name || textCapitalize(key);
const statusOf = (key) => DOCUMENT_STATUSES.find((s) => s.key === key) || DOCUMENT_STATUSES[0];

const DocumentsTab = () => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [query, setQuery] = useState("");
    const [refresh, setRefresh] = useState(0);
    const [status, setStatus] = useState("");
    const [typeInput, setTypeInput] = useState("");
    const [documentType, setDocumentType] = useState("");
    const [site, setSite] = useState("");
    const [data, setData] = useState({ list: [], total: 0, summary: {} });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState(null);
    const [history, setHistory] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [confirm, setConfirm] = useState(null);

    const applyFilters = () => {
        setQuery(search.trim());
        setDocumentType(typeInput.trim().toLowerCase());
        setPage(1);
    };

    const clearFilters = () => {
        setSearch("");
        setTypeInput("");
        setQuery("");
        setDocumentType("");
        setSite("");
        setStatus("");
        setPage(1);
    };

    const onFilterKey = (e) => { if (e.key === "Enter") { e.preventDefault(); applyFilters(); } };
    const filtersActive = Boolean(query || documentType || site || status || search.trim() || typeInput.trim());

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/admin/document/list/${page - 1}`,
            params: { count: PAGE_SIZE },
            data: {
                ...(query ? { search: query } : {}),
                ...(status ? { status } : {}),
                ...(documentType ? { documentType } : {}),
                ...(site ? { site } : {}),
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0, summary: {} });
        } else {
            setData((previous) => ({ list: [], total: 0, summary: previous.summary || {} }));
            setError(response?.data?.message || "Unable to load the documents");
        }
        setLoading(false);
    }, [page, query, status, documentType, site, refresh]);

    useEffect(() => { load(); }, [load]);

    const changeFilter = (setter) => (e) => { setter(e.target.value); setPage(1); };

    const askStatus = (row, next) => { if (next !== row.status) setConfirm({ kind: "status", row, next }); };

    const changeStatus = async (row, next) => {
        if (next === row.status) return;
        setBusyId(row.documentId);
        const response = await Handler({ method: "patch", url: `/admin/document/${row.documentId}/status`, data: { status: next } });
        setBusyId(null);
        if (response?.success) {
            toast.success(response?.data?.message || "Updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to change the status"));
        }
    };

    const remove = async (row) => {
        setBusyId(row.documentId);
        const response = await Handler({ method: "delete", url: `/admin/document/${row.documentId}` });
        setBusyId(null);
        if (response?.success) {
            toast.success(response?.data?.message || "Document deleted");
            if (data.list.length === 1 && page > 1) setPage(page - 1); else load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to delete this document"));
        }
    };

    const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
    const summary = data.summary || {};
    const allCount = DOCUMENT_STATUSES.reduce((sum, s) => sum + (summary[s.key] || 0), 0);

    return (
        <div className="settings-card">
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
                <div>
                    <h5 className="fw-semibold mb-1">Documents</h5>
                    <p className="text-muted small mb-0">Policies, terms and other written documents. Pick the site each one is shown on and publish it when it is ready.</p>
                </div>
                <button type="button" className="btn btn-primary" onClick={() => setForm({ documentId: null })}>New document</button>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3" role="tablist" aria-label="Document status">
                <button type="button" role="tab" aria-selected={status === ""} className={`btn btn-sm ${status === "" ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => { setStatus(""); setPage(1); }}>All <span className="ms-1 opacity-75">{allCount}</span></button>
                {DOCUMENT_STATUSES.map((s) => (
                    <button key={s.key} type="button" role="tab" aria-selected={status === s.key} className={`btn btn-sm ${status === s.key ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => { setStatus(s.key); setPage(1); }}>{s.name} <span className="ms-1 opacity-75">{summary[s.key] || 0}</span></button>
                ))}
            </div>

            <div className="row g-2 mb-3">
                <div className="col-12 col-md-4">
                    <input type="search" className="form-control" placeholder="Search by name, ID, link name or author..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={onFilterKey} aria-label="Search documents" />
                </div>
                <div className="col-6 col-md-3">
                    <input type="search" className="form-control" placeholder="Filter by type..." value={typeInput} onChange={(e) => setTypeInput(e.target.value)} onKeyDown={onFilterKey} aria-label="Filter by type" />
                </div>
                <div className="col-6 col-md-2">
                    <select className="form-select h-100" value={site} onChange={changeFilter(setSite)} aria-label="Filter by site">
                        <option value="">All sites</option>
                        {DOCUMENT_SITES.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                    </select>
                </div>
                <div className="col-12 col-md-3 d-flex gap-2">
                    <button type="button" className="btn btn-primary flex-grow-1" onClick={applyFilters}>Search</button>
                    <button type="button" className="btn btn-outline-secondary flex-grow-1" onClick={clearFilters} disabled={!filtersActive}>Clear</button>
                </div>
            </div>

            {loading ? (
                <>
                    <Skeleton height={38} className="mb-2" />
                    <Skeleton height={38} className="mb-2" />
                    <Skeleton height={38} className="mb-2" />
                </>
            ) : error ? (
                <div className="text-center text-muted py-5">
                    <div className="mb-2">{error}</div>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load}>Retry</button>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table align-middle">
                        <thead>
                            <tr>
                                <th>Document</th>
                                <th>Type</th>
                                <th>Site</th>
                                <th>Version</th>
                                <th>Status</th>
                                <th>Updated</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!data.list.length ? (
                                <tr><td colSpan={7} className="text-center text-muted py-4">{filtersActive ? "No documents match these filters" : "No documents yet"}</td></tr>
                            ) : null}
                            {data.list.map((row) => {
                                const current = statusOf(row.status);
                                return (
                                    <tr key={row.documentId}>
                                        <td>
                                            <div className="fw-semibold">{row.documentName}</div>
                                            <div className="text-muted small">{row.documentId}{row.seo_name ? <> · <span className="font-monospace">{row.seo_name}</span></> : null}</div>
                                            {row.authorName ? <div className="text-muted small">By {row.authorName}</div> : null}
                                        </td>
                                        <td><span className="font-monospace">{row.documentType || "-"}</span></td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {(row.site || []).map((s) => <span key={s} className="badge text-bg-light border">{siteName(s)}</span>)}
                                            </div>
                                        </td>
                                        <td>{row.version || "-"}{row.latestVersion && row.latestVersion !== row.version ? <div className="text-muted small">Latest {row.latestVersion} (not published)</div> : null}</td>
                                        <td>
                                            <select className={`form-select form-select-sm w-auto fw-semibold ${current.key === "published" ? "border-success text-success" : current.key === "pending" ? "border-warning" : ""}`} value={current.key} disabled={busyId === row.documentId} onChange={(e) => askStatus(row, e.target.value)} aria-label={`Status of ${row.documentName}`}>
                                                {DOCUMENT_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                                            </select>
                                        </td>
                                        <td className="text-nowrap">{row.updatedAt ? formatDate(row.updatedAt) : "-"}</td>
                                        <td>
                                            <div className="d-flex gap-2 justify-content-end">
                                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setForm({ documentId: row.documentId })}>Edit</button>
                                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setHistory({ documentId: row.documentId, documentName: row.documentName })}>History</button>
                                                {/* <button type="button" className="btn btn-sm btn-outline-danger" disabled={busyId === row.documentId || row.status === "published"} title={row.status === "published" ? "Unpublish before deleting" : undefined} onClick={() => setConfirm({ kind: "delete", row })}>Delete</button> */}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && !error && data.total > PAGE_SIZE ? (
                <div className="d-flex align-items-center justify-content-between">
                    <span className="text-muted small">{data.total} documents</span>
                    <div className="d-flex align-items-center gap-2">
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                        <span className="small">Page {page} of {totalPages}</span>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
                    </div>
                </div>
            ) : null}

            {confirm ? (
                <DocumentConfirmModal
                    title={confirm.kind === "delete" ? "Delete this document?" : confirm.next === "published" ? "Publish this document?" : `Mark this document ${confirm.next}?`}
                    message={confirm.kind === "delete"
                        ? `"${confirm.row.documentName}" and all of its versions will be deleted. This cannot be undone.`
                        : confirm.next === "published"
                            ? `The latest version of "${confirm.row.documentName}" becomes the live text. Any version that is published now will move to Pending.`
                            : `"${confirm.row.documentName}" will be marked ${confirm.next}.`}
                    confirmLabel={confirm.kind === "delete" ? "Delete" : confirm.next === "published" ? "Publish" : "Confirm"}
                    tone={confirm.kind === "delete" ? "danger" : "primary"}
                    busy={Boolean(busyId)}
                    onConfirm={async () => {
                        const { kind, row, next } = confirm;
                        if (kind === "delete") await remove(row); else await changeStatus(row, next);
                        setConfirm(null);
                    }}
                    onCancel={() => setConfirm(null)}
                />
            ) : null}

            {history ? (
                <DocumentHistoryModal
                    key={history.documentId}
                    documentId={history.documentId}
                    documentName={history.documentName}
                    onClose={() => setHistory(null)}
                    onChanged={load}
                />
            ) : null}

            {form ? (
                <DocumentFormModal
                    key={form.documentId || "new"}
                    documentId={form.documentId}
                    onClose={() => setForm(null)}
                    onSaved={() => { setForm(null); setPage(1); setRefresh((n) => n + 1); }}
                />
            ) : null}
        </div>
    );
};

export default DocumentsTab;
